import type React from 'react';
import { useReducer } from 'react';

import { CheckIcon } from '@vlabs/icons';
import { Control, Dropdown, DropdownItem } from '@vlabs/uikit';
import type { TOption } from '@vlabs/uikit/types';
import equal from 'fast-deep-equal';

type TDropdownSelectValue = string | number | object;

interface DropdownSelectProps<T extends TDropdownSelectValue = string> {
  options: TOption<T>[];
  onSelect: (value: T) => void;
  label: string;
  value: T | T[];
  icon?: React.ReactElement;
  disabled?: boolean;
}

function isSelectedValue<T extends TDropdownSelectValue>(currentValue: T | T[], optionValue: T): boolean {
  if (Array.isArray(currentValue)) {
    return currentValue.some((item) => equal(item, optionValue));
  }

  return equal(currentValue, optionValue);
}

function DropdownSelect<T extends TDropdownSelectValue = string>({
  options,
  onSelect,
  label,
  value,
  icon = undefined,
  disabled = false,
}: DropdownSelectProps<T>): React.JSX.Element {
  const [isOpen, toggleOpen] = useReducer((state) => !state, false);

  return (
    <Dropdown
      isOpen={isOpen}
      onClose={toggleOpen}
      options={options.map((option) => {
        const selected = isSelectedValue(value, option.value);
        return (
          <DropdownItem
            key={JSON.stringify(option.value)}
            label={option.label}
            onClick={() => {
              onSelect(option.value);
              toggleOpen();
            }}
            rightIcon={selected ? <CheckIcon /> : undefined}
            selected={selected}
          />
        );
      })}
    >
      <Control.Button
        disabled={disabled}
        icon={icon}
        kind="secondary"
        onClick={toggleOpen}
        size="small"
        variant="filled"
      >
        {label}
      </Control.Button>
    </Dropdown>
  );
}

export { DropdownSelect };
